import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, TrendingDown, Activity, Eye, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface KeywordMetric {
  keyword: string;
  currentRanking: number;
  previousRanking: number;
  change: number;
  page_url: string;
}

export const RealTimeMetrics = () => {
  const [metrics, setMetrics] = useState<KeywordMetric[]>([]);
  const [summary, setSummary] = useState({
    totalKeywords: 0,
    improving: 0,
    declining: 0,
    stable: 0
  });

  useEffect(() => {
    fetchMetrics();
    
    // Set up real-time subscription
    const channel = supabase
      .channel('keyword-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'seo_keywords'
        },
        () => {
          fetchMetrics();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchMetrics = async () => {
    const { data: keywords } = await supabase
      .from('seo_keywords')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(10);

    if (keywords) {
      const metricsData: KeywordMetric[] = keywords.map((kw) => {
        const previous = kw.target_ranking || 100;
        const current = kw.current_ranking || previous;
        return {
          keyword: kw.keyword,
          currentRanking: current,
          previousRanking: previous,
          change: previous - current,
          page_url: kw.page_url
        };
      });

      setMetrics(metricsData);

      const improving = metricsData.filter(m => m.change > 0).length;
      const declining = metricsData.filter(m => m.change < 0).length;
      const stable = metricsData.filter(m => m.change === 0).length;

      setSummary({
        totalKeywords: metricsData.length,
        improving,
        declining,
        stable
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="w-4 h-4" />
              Total Keywords
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalKeywords}</div>
            <p className="text-xs text-muted-foreground">Tracked keywords</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-green-600">
              <TrendingUp className="w-4 h-4" />
              Improving
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summary.improving}</div>
            <p className="text-xs text-muted-foreground">Ranking up</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-600">
              <TrendingDown className="w-4 h-4" />
              Declining
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{summary.declining}</div>
            <p className="text-xs text-muted-foreground">Ranking down</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Stable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.stable}</div>
            <p className="text-xs text-muted-foreground">No change</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Real-Time Keyword Rankings
          </CardTitle>
          <CardDescription>
            Live updates of keyword position changes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {metrics.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No keyword data available</p>
            ) : (
              metrics.map((metric, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors">
                  <div className="flex-1">
                    <p className="font-medium">{metric.keyword}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[300px]">{metric.page_url}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-semibold">Rank #{metric.currentRanking}</p>
                      <p className="text-xs text-muted-foreground">Current</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {metric.change > 0 ? (
                        <>
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          <Badge variant="default" className="bg-green-600">
                            +{metric.change}
                          </Badge>
                        </>
                      ) : metric.change < 0 ? (
                        <>
                          <TrendingDown className="w-4 h-4 text-red-600" />
                          <Badge variant="destructive">
                            {metric.change}
                          </Badge>
                        </>
                      ) : (
                        <Badge variant="secondary">
                          No change
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
